import * as React from 'react';
import type {
  Params,
  RouterHistory,
  GetElement,
  ElementContext,
  LocationArg,
} from 'src/rerouter';
import {useHistory} from 'src/rerouter';

import {useFullScreenApp} from 'src/hooks/useFullScreenApp';

import Loading from 'src/components/lib/loading';
import {AnalyticsService} from 'src/analytics';


export function container(
  Component: React.ComponentType<{
    children: React.ReactNode;
  }>,
): GetElement {
  return ({children, pathname}) => (
    <Component key={pathname} children={children} />
  );
}

export function imbue<C extends ElementContext>(
  Component: React.ComponentType<C>,
  suspenseProps?: {
    fallback: React.ReactNode;
  },
): GetElement {
  return (context: ElementContext) => {
    if (suspenseProps) {
      return (
        <React.Suspense fallback={suspenseProps.fallback}>
          <Component {...(context as C)} key={context.pathname} />
        </React.Suspense>
      );
    }
    return <Component {...(context as C)} key={context.pathname} />;
  };
}

export function imbueLegacy<
  C extends ElementContext & {
    router: RouterHistory;
  },
>(Component: React.ComponentType<C>): GetElement {
  return (context: ElementContext) => (
    // @ts-expect-error - TS2769 - No overload matches this call.
    <Component {...context} router={context.history} key={context.pathname} />
  );
}

export function redirect(to: LocationArg) {
  return (
    <OnEnter
      // @ts-expect-error - TS2322 - Type '(history: BaseHistory) => void' is not assignable to type '(arg1: BaseHistory, arg2: null) => (() => void) | undefined'.
      callback={(history) => {
        history.replace(to);
      }}
      params={null}
    />
  );
}

export function trackPageView(
  forceChildren: GetElement | React.ReactNode = null,
): GetElement {
  return (context: ElementContext) => {
    const finalChildren =
      typeof forceChildren === 'function'
        ? forceChildren(context)
        : forceChildren;
    function fireEvent() {
      try {
        AnalyticsService.track('page_view', {
          url: context.location.pathname,
        });
      } catch (error: any) {}
    }
    const {children, pathname, params} = context;
    return (
      <OnEnter
        key={pathname}
        params={params}
        // @ts-expect-error - TS2322 - Type '() => void' is not assignable to type '(arg1: BaseHistory, arg2: Readonly<{ [key: string]: string; }>) => (() => void) | undefined'.
        callback={fireEvent}
        children={finalChildren || children}
      />
    );
  };
}

export function onEnter(
  callback: (arg1: RouterHistory, arg2: Params) => undefined | (() => void),
  forceChildren: GetElement | React.ReactNode = null,
): GetElement {
  return (context: ElementContext) => {
    const finalChildren =
      typeof forceChildren === 'function'
        ? forceChildren(context)
        : forceChildren;
    const {children, pathname, params} = context;
    return (
      <OnEnter
        key={pathname}
        params={params}
        callback={callback}
        children={finalChildren || children}
      />
    );
  };
}

export function onEnterAsync(
  callback: (arg1: RouterHistory, arg2: Params) => Promise<unknown>,
  forceChildren: React.ReactNode = null,
): GetElement {
  // @ts-expect-error - TS2345 - Argument of type '(history: BaseHistory, params: Readonly<{ [key: string]: string; }>) => void' is not assignable to parameter of type '(arg1: BaseHistory, arg2: Readonly<{ [key: string]: string; }>) => (() => void) | undefined'.
  return onEnter((history, params) => {
    callback(history, params);
  }, forceChildren);
}

// TODO (kyle): there is a possible bug here. `onEnter` will not fire when
// moving between two `OnEnter` components in the same place in the tree.
export function OnEnter<P>({
  callback,
  params,
  children = null,
}: {
  callback: (arg1: RouterHistory, arg2: P) => undefined | (() => void);
  params: P;
  children?: React.ReactNode;
}) {
  const history = useHistory();
  React.useEffect(() => callback(history, params), []);
  return children;
}

//NOTE:(diwakersurya) this function can be used while defining route to decide whether
// the experience should be without top and left nav.
export function fullscreen<C extends ElementContext>(
  Component: React.ComponentType<C>,
): GetElement {
  return (context: ElementContext) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    const children = <Component {...context} key={context.pathname} />;

    return <FullScreen>{children}</FullScreen>;
  };
}

export function FullScreen({children = null}: {children?: React.ReactNode}) {
  useFullScreenApp();
  return children;
}

type SplitComponent<C> = ((arg1: C) => React.ReactNode) & {
  preload: () => Promise<{default: React.ComponentType<C>}>;
};

export function split<C>(
  importer: () => Promise<{
    default: React.ComponentType<C>;
  }>,
): SplitComponent<C> {
  const SplitComponent = React.lazy(importer);
  const Component = (props: C) => (
    <React.Suspense fallback={<Loading />}>
      {/* @ts-expect-error - TS2322 - Type 'C' is not assignable to type 'IntrinsicAttributes & (C | (PropsWithoutRef<C> & RefAttributes<Component<C, any, any>>))'. */}
      <SplitComponent {...props} />
    </React.Suspense>
  );
  Component.preload = importer;
  return Component;
}
